import { useMemo } from 'react';
import { HubLessonCard } from '@/components/Hub/HubLessonCard';
import { defineHubSection, type HubSectionData } from '@/components/Hub/hubSectionTypes';
import { HUB_LESSONS_PATH } from '@/components/Hub/hubRoutes';
import { shuffled } from '@/components/Hub/hubShuffle';
import { lessonStateOf } from '@/pages/Explore/Lessons/lessonState';
import { useLaunchLesson } from '@/pages/Explore/Lessons/useLaunchLesson';
import { useGetMyCmi5RegistrationsQuery, useListCmi5CoursesQuery } from '@/services/ApiService';

const MAX_LESSONS = 6;

const useLessonsData = (): HubSectionData => {
    const { data: courses = [], isLoading, isError } = useListCmi5CoursesQuery();
    const { data: registrations = [] } = useGetMyCmi5RegistrationsQuery();
    const launch = useLaunchLesson();

    // Shuffled apart from the slides so a registrations refetch doesn't reorder the carousel.
    const picked = useMemo(() => shuffled(courses).slice(0, MAX_LESSONS), [courses]);
    const slides = useMemo(
        () => picked.map(course => ({
            id: course.id,
            render: () => (
                <HubLessonCard
                    title={course.title}
                    description={course.description}
                    imageUrl={course.coverImageUrl ?? undefined}
                    state={lessonStateOf(registrations.find(r => r.courseId === course.id))}
                    onPlay={() => launch(course)}
                />
            ),
        })),
        [picked, registrations, launch],
    );

    return { slides, isLoading, isError };
};

export const lessonsSection = defineHubSection({
    id: 'lessons',
    title: 'Lessons',
    blurb: 'Build durable skills you can use for a variety of careers.',
    viewAll: { to: HUB_LESSONS_PATH, label: 'View all' },
    endCard: {
        to: HUB_LESSONS_PATH,
        heading: 'Check out some more Lessons',
        actionLabel: 'Go to Explore page',
        groundClassName: 'bg-gz-marine-100',
        zigzagClassName: 'bg-gz-marine-200',
        zigzagOpacityClassName: 'opacity-30',
    },
    itemBasisClassName: 'basis-[86%] sm:basis-[82%]',
    surfaceClassName: 'rounded-xl light:bg-gz-surface-dark dark:bg-gz-bluebell-500',
    useData: useLessonsData,
});
